import React from "react";
import { Link } from "react-router-dom";

const InterestRatePolicy = () => {
  return (
    <>
      {/* Custom Cursors */}
      <div className="custom-cursor-one"></div>
      <div className="custom-cursor-two"></div>

      {/* Hero */}
      <div
        className="inner-page-hero"
        style={{ backgroundImage: "url(/assets/images/background/bg3.png)" }}
      >
        <div className="container">
          <div className="hero-heading-title">
            <h2>Interest Rate Policy</h2>
          </div>

          <ul className="bradcrumb">
            <li><a href="/">Home</a></li>
            <li><Link to="#">Interest Rate Policy</Link></li>
          </ul>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <section className="privacy-section">
        <div className="container">
          <div className="row justify-content-center">

            <div className="col-lg-12">
              <div className="privacy-content-card">

                {/* Header */}
                <div className="mb-5 text-center">

                  <span className="heading-subtitle wow fadeInUp animated">
                    💰 Interest Rate Policy
                  </span>

                  <h2 className="wow fadeInUp animated">
                    Wall Street Finz — Interest Rate & Charges Policy
                  </h2>

                  <div className="meta-info-box">
                    <p><strong>Effective Date:</strong> Immediately upon publication</p>
                    <p><strong>Policy Version:</strong> 2.0 | Last Updated: January 2025</p>
                    <p><strong>Compliance:</strong> RBI Fair Practices Code | Digital Lending Guidelines 2023</p>
                  </div>

                  <p className="intro-text mt-4">
                    This Interest Rate Policy outlines the principles, methodology,
                    and transparency standards followed by <strong>Wall Street Finz </strong>
                    in determining interest rates and charges for lending products.
                    We follow fair, transparent, and risk-based pricing in full
                    compliance with regulatory requirements.
                  </p>

                </div>

                <hr className="divider" />

                {/* 1 Objective */}
                <div className="policy-block">
                  <h3>1. Objective of This Policy</h3>

                  <div className="info-grid">

                    <div className="info-card">
                      <h4>Transparency</h4>
                      <p>Clear disclosure of all interest rates and charges upfront.</p>
                    </div>

                    <div className="info-card">
                      <h4>Fair Pricing</h4>
                      <p>Risk-based pricing reflecting borrower profile and market conditions.</p>
                    </div>

                    <div className="info-card">
                      <h4>Regulatory Compliance</h4>
                      <p>Adherence to RBI guidelines and fair lending practices.</p>
                    </div>

                  </div>

                  <p className="mt-3">
                    The objective is to establish a structured framework for determining
                    interest rates that balances market competitiveness, risk assessment,
                    and regulatory requirements while ensuring customers receive clear
                    pricing information.
                  </p>

                </div>

                {/* 2 Governance */}
                <div className="policy-block">
                  <h3>2. Governance & Approval Structure</h3>

                  <div className="info-grid">

                    <div className="info-card">
                      <h4>Board of Directors</h4>
                      <p>Final approval of interest rate policy framework.</p>
                      <p className="small text-muted">Annual review</p>
                    </div>

                    <div className="info-card">
                      <h4>Pricing Committee (ALCO)</h4>
                      <p>Quarterly review of benchmark rates and spreads.</p>
                      <p className="small text-muted">CEO, CFO, Risk Head, Compliance Officer</p>
                    </div>

                    <div className="info-card">
                      <h4>Business Units</h4>
                      <p>Implementation within approved policy framework.</p>
                      <p className="small text-muted">Monthly reporting</p>
                    </div>

                  </div>
                </div>

                {/* 3 Determinants */}
                <div className="policy-block">
                  <h3>3. Interest Rate Determinants & Methodology</h3>

                  <p>Interest rates follow a risk-based pricing model considering the following factors:</p>

                  <div className="info-grid">

                    <div className="info-card">
                      <h4>Cost-Based Factors</h4>
                      <ul>
                        <li>Cost of borrowed funds</li>
                        <li>Liquidity premiums</li>
                        <li>Operational and compliance costs</li>
                        <li>Market liquidity conditions</li>
                      </ul>
                    </div>

                    <div className="info-card">
                      <h4>Risk-Based Factors</h4>
                      <ul>
                        <li>Credit score (CIBIL/Experian)</li>
                        <li>Income stability</li>
                        <li>Loan tenure and purpose</li>
                        <li>Digital behavior score</li>
                        <li>Geographic risk</li>
                      </ul>
                    </div>

                    <div className="info-card">
                      <h4>Market Factors</h4>
                      <ul>
                        <li>Competitor benchmarking</li>
                        <li>Regulatory compliance costs</li>
                        <li>Customer relationship value</li>
                        <li>Product pricing strategies</li>
                      </ul>
                    </div>

                  </div>
                </div>

                {/* 4 Rate Types */}
                <div className="policy-block">
                  <h3>4. Interest Rate Types & Structures</h3>

                  <div className="info-grid">

                    <div className="info-card">
                      <h4>Fixed Interest Rate</h4>
                      <ul>
                        <li>Constant throughout loan tenure</li>
                        <li>Used for short-term personal loans</li>
                        <li>No exposure to market fluctuations</li>
                        <li>Tenure up to 36 months</li>
                      </ul>
                    </div>

                    <div className="info-card">
                      <h4>Floating Interest Rate</h4>
                      <ul>
                        <li>Linked to benchmark rate</li>
                        <li>Reset frequency: Quarterly</li>
                        <li>Linked to RBI Repo Rate</li>
                        <li>Tenure above 36 months</li>
                      </ul>
                    </div>

                    <div className="info-card">
                      <h4>Reducing Balance Method</h4>
                      <ul>
                        <li>Interest calculated on daily reducing balance</li>
                        <li>Transparent computation</li>
                        <li>Lower effective interest cost</li>
                        <li>Standard method for all products</li>
                      </ul>
                    </div>

                  </div>
                </div>

                {/* 5 Charges Table */}
                <div className="policy-block">
                  <h3>5. Charges, Fees & Penal Interest</h3>

                  <div className="table-responsive">
                    <table className="table policy-table">
                      <thead>
                        <tr>
                          <th>Charge Type</th>
                          <th>Description</th>
                          <th>Amount / Rate</th>
                          <th>When Applicable</th>
                        </tr>
                      </thead>

                      <tbody>
                        <tr>
                          <td>Processing Fee</td>
                          <td>Loan application processing</td>
                          <td>Up to 2% of loan amount</td>
                          <td>At loan application</td>
                        </tr>

                        <tr>
                          <td>Penal Interest</td>
                          <td>Delayed EMI payments</td>
                          <td>2% per month</td>
                          <td>After 5 days from EMI due date</td>
                        </tr>

                        <tr>
                          <td>Prepayment Charges</td>
                          <td>Early loan closure</td>
                          <td>0-2%</td>
                          <td>On prepayment</td>
                        </tr>

                        <tr>
                          <td>Bouncing Charges</td>
                          <td>Failed auto-debit</td>
                          <td>₹500 per instance</td>
                          <td>Each failed payment</td>
                        </tr>

                        <tr>
                          <td>Documentation Charges</td>
                          <td>Legal & documentation work</td>
                          <td>₹1000-₹2000</td>
                          <td>At sanction</td>
                        </tr>

                        <tr>
                          <td>Statement Charges</td>
                          <td>Duplicate statements</td>
                          <td>₹100</td>
                          <td>On request</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <p className="small text-muted mt-2">
                    All charges are exclusive of GST. Penal interest applies only on overdue amounts.
                  </p>

                </div>

                {/* 6 Transparency */}
                <div className="policy-block">
                  <h3>6. Transparency & Customer Disclosure</h3>

                  <ul className="check-list">
                    <li>APR disclosed in sanction letter</li>
                    <li>Complete schedule of charges</li>
                    <li>EMI breakdown showing principal & interest</li>
                    <li>Total interest payable disclosed</li>
                    <li>Loan agreement clarity with bold charges</li>
                    <li>7 days notice for interest rate changes</li>
                    <li>Digital access to all loan documents</li>
                  </ul>

                </div>

                {/* 7 Customer Rights */}
                <div className="policy-block">
                  <h3>7. Customer Rights & Grievance Redressal</h3>

                  <ul className="check-list">
                    <li>Right to clear interest rate information</li>
                    <li>Right to receive APR and charges in writing</li>
                    <li>Right to prepay loan</li>
                    <li>Right to notice for rate changes</li>
                    <li>Right to grievance redressal</li>
                  </ul>

                  <div className="alert-box warning mt-3">
                    For disputes regarding interest rates or charges:
                    <br />
                    Email: info@wallstreetfinz.com
                    <br />
                    Helpline: +91-99119-98997
                    <br />
                    Resolution Commitment: 6 working days
                  </div>

                </div>

                {/* 8 Policy Review */}
                <div className="policy-block">
                  <h3>8. Policy Review & Updates</h3>

                  <ul className="check-list">
                    <li>Quarterly market benchmark review</li>
                    <li>Half-yearly regulatory compliance check</li>
                    <li>Annual comprehensive policy review</li>
                  </ul>

                  <p>
                    Any changes to interest rates or charges will be communicated
                    to customers with 30 days notice and will apply prospectively.
                  </p>

                </div>

                {/* Contact */}
                <div className="contact-block">
                  <h3>Contact Us</h3>

                  <p>
                    For questions, concerns, or requests related to pricing or privacy:
                  </p>

                  <div className="contact-details">
                    <div className="contact-row">
                      <i className="flaticon-email"></i>
                      <a href="mailto:info@wallstreetfinz.com">
                        info@wallstreetfinz.com
                      </a>
                    </div>
                  </div>

                </div>

              </div>
            </div>

          </div>
        </div>
      </section>
    </>
  );
};

export default InterestRatePolicy;